import React, { useState } from "react";
import { X, Eye, Mail, Code, Monitor } from "lucide-react";

interface PreviewTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: any;
}

const PreviewTemplateModal: React.FC<PreviewTemplateModalProps> = ({
  isOpen,
  onClose,
  template,
}) => {
  const [viewMode, setViewMode] = useState<"preview" | "code">("preview");

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Preview Template
                </h3>
                <p className="text-sm text-gray-500">{template.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <div className="bg-gray-50 rounded-lg p-3 border">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-900">{template.subject}</span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Content {viewMode === "preview" ? "Preview" : "Source"}
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode("preview")}
                    className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm transition-colors ${
                      viewMode === "preview"
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <Monitor className="h-4 w-4" />
                    Preview
                  </button>
                  <button
                    onClick={() => setViewMode("code")}
                    className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm transition-colors ${
                      viewMode === "code"
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <Code className="h-4 w-4" />
                    Code
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border min-h-[200px]">
                {viewMode === "preview" ? (
                  <div className="bg-white rounded-lg shadow-sm border p-4 max-w-2xl mx-auto">
                    <style>
                      {`
                        .email-preview * {
                          max-width: 100% !important;
                        }
                        .email-preview img {
                          max-width: 100% !important;
                          height: auto !important;
                        }
                        .email-preview table {
                          width: 100% !important;
                          border-collapse: collapse !important;
                        }
                        .email-preview td, .email-preview th {
                          padding: 8px !important;
                        }
                      `}
                    </style>
                    <div
                      className="email-preview"
                      dangerouslySetInnerHTML={{ __html: template.content }}
                      style={{
                        fontFamily: "Arial, sans-serif",
                        lineHeight: "1.6",
                        color: "#333",
                      }}
                    />
                  </div>
                ) : (
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap">
                      {template.content}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* Template Info */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Type
                </label>
                <span className="text-sm text-gray-600">
                  {template.type || "Custom"}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created
                </label>
                <span className="text-sm text-gray-600">
                  {new Date(template.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewTemplateModal;
